import { Fragment, useState, useEffect } from "react";
import { notification, Button, Modal, Select } from "antd";
import { TitleHeader, Searcher, TableAntd } from "../../components";
import { ReloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Redirect } from "react-router-dom";
import { useAuth } from "../../utils/useAuth";
import { executeQuery, executeUpdate } from "../../api/sistema";
import {
  isPorSedeEnabled,
  getAllSedes,
  isPorPeriodoEnabled,
  getAllPeriodos
} from "../../api/auxiliar";
import { createColumns } from "./formatTable";

function ModelFile() {
  const { isRegistrador } = useAuth();

  const [filterTable, setFilterTable] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalAsistencias, setTotalAsistencias] = useState(0);

  const [dataToTable, setDataToTable] = useState([]);
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [porSedeEnabled, setPorSedeEnabled] = useState(false);
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState(null);

  const [porPeriodoEnabled, setPorPeriodoEnabled] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState(null);

  useEffect(() => {
    fetchDataToTable();
    fetchFiltersConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const fetchDataToTable = async () => {
    setIsLoading(true);

    executeQuery({
      query: "get_all_asistencias_v2",
      collection: "pos_postulante"
    })
      .then((res) => {
        setDataToTable(res.payload);
        calculaTotalAsistencias(res.payload);
      })
      .catch((err) => {
        notification.error({
          message: "Problemas con la solicitud",
          description: err.message
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const calculaTotalAsistencias = (data) => {
    const total = data.filter((i) => i.asistio === true);
    setTotalAsistencias(total.length || 0);
  };

  const fetchFiltersConfig = async () => {
    try {
      const sedeConfig = await isPorSedeEnabled();
      setPorSedeEnabled(sedeConfig.payload.porSedeEnabled);
      
      if (sedeConfig.payload.porSedeEnabled) {
        const sedesRes = await getAllSedes();
        setSedes(sedesRes.payload);
      }

      const periodoConfig = await isPorPeriodoEnabled();
      setPorPeriodoEnabled(periodoConfig.payload.porPeriodoEnabled);

      if (periodoConfig.payload.porPeriodoEnabled) {
        const periodosRes = await getAllPeriodos();
        setPeriodos(periodosRes.payload);
      }
    } catch (err) {
      console.error("Error al obtener configuración de filtros:", err);
    }
  };

  if (isRegistrador) return <Redirect to="/app" />;

  const handleDelete = (record) => {
    Modal.confirm({
      title: `¿Estás seguro de eliminar la asistencia de '${record.nombre}?'`,
      icon: <ExclamationCircleOutlined />,
      okText: "Sí, eliminar",
      cancelText: "Cancelar",
      onOk() {
        executeUpdate(
          {
            collection: "pos_asistencia",
            nombreid: "_id",
            valorid: record._id
          },
          {
            eliminado: 1
          }
        )
          .then((res) => {
            if (res.success) {
              notification.success({
                message: "Operación exitosa",
                description: `Se ha eliminado la asistencia satistactoriamente.`
              });
              fetchDataToTable();
            }
          })
          .catch(() => {
            notification.error({
              message: "Operación no válida",
              description: `No se pudo realizar la acción, comuníquese con el administrador.`
            });
          });
      }
    });
  };

  const columnsToTable = createColumns(
    page,
    pageSize,
    handleDelete,
    porSedeEnabled,
    porPeriodoEnabled
  );

  const search = (value) => {
    const tempFilterTable = dataToTable.filter((o) =>
      Object.keys(o).some((k) => String(o[k]).toLowerCase().includes(value.toLowerCase()))
    );

    setFilterTable(tempFilterTable);
  };

  const handleSedeChange = (value) => {
    setSelectedSede(value);
  };

  const handlePeriodoChange = (value) => {
    setSelectedPeriodo(value);
  };

  const getFilteredData = () => {
    let data = filterTable == null ? dataToTable : filterTable;
    
    if (porSedeEnabled && selectedSede) {
      data = data.filter(item => item.idsede === selectedSede);
    }

    if (porPeriodoEnabled && selectedPeriodo) {
      data = data.filter((item) => item.idperiodo === selectedPeriodo);
    }
    
    return data;
  };

  const getFilteredAsistencias = () => {
    const filtered = getFilteredData();
    return filtered.filter((i) => i.asistio === true).length;
  };

  return (
    <Fragment>
      <TitleHeader
        title={
          <span className="flex gap-2 items-center">
            <h1 className="font-bold">Asistencias ({getFilteredAsistencias()})</h1>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => setReload(!reload)}
            ></Button>
          </span>
        }
      />

      <div className="flex gap-2 mb-4 flex-col md:flex-row">
        <Searcher onSearch={search} placeholder="Busca algún postulante" />
        
        {porSedeEnabled && (
          <Select
            placeholder="Filtrar por sede"
            allowClear
            style={{ width: 250 }}
            onChange={handleSedeChange}
            options={[
              { value: null, label: "Todas las sedes" },
              ...sedes.map(sede => ({
                value: sede.idsede,
                label: sede.nombre
              }))
            ]}
          />
        )}

        {porPeriodoEnabled && (
          <Select
            placeholder="Filtrar por periodo"
            allowClear
            style={{ width: 250 }}
            onChange={handlePeriodoChange}
            options={[
              { value: null, label: "Todos los periodos" },
              ...periodos.map((periodo) => ({
                value: periodo.idperiodo,
                label: `${periodo.nombre}${periodo.siglas ? ` (${periodo.siglas})` : ""}`
              }))
            ]}
          />
        )}
      </div>

      <TableAntd
        columns={columnsToTable}
        dataSource={getFilteredData()}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isLoading}
      />
    </Fragment>
  );
}

export default ModelFile;
